import { useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import type { PetOrigin, PetSpecies, ReceptionBranch, ReceptionSession } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { queryKeys } from "@/shared/query/queryClient";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { useSessionState } from "@/shared/session/onboarding";
import { HouseholdProvider, useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Button, DataOriginBadge, ErrorState, Icon, LoadingState, Page, TopBar, avatarChain, speciesArtLabel, useAvatarStep } from "@/shared/ui";
import { EntrySteps, entryStepKicker } from "@/features/identity/EntryHeading";
import receptionWorld from "./assets/reception-world-v1.webp";
import { PawBadge } from "@/features/pets/PetPortrait";
import { SpeciesIllustration } from "@/features/pets/SpeciesIllustration";
import { petToReturnTo } from "@/features/world_map/WorldGate";
import { hasInAppHistory, leaveSupplement, SUPPLEMENT_PARENT, trailFromReception } from "./trail";
import { useReceptionScope, useReceptionGuard, type ReceptionScope, type ReceptionAttempt } from "./receptionScope";
import "./reception.css";

/**
 * 接待页头像：不用名字首字——有照片用照片（打不开就往下退），演示模式用授权小灰猫；没有照片（或照片打不开）时，
 * 领养来的原创居民用居民页同一张物种插画（portraitFallback：读屏说是插画、不是 TA 本人的照片），其余是爪印占位。
 * origin / species 拿不到就是 null：不猜来历，落到爪印。
 * 退回走全站共用的那条链（shared/ui 的 avatarChain + useAvatarStep，2026-09-27 D4 第三批统一写法）：照片 → 演示小灰猫（只在演示模式）→ 插画或爪印；
 * 爪印是全站同一个徽章（UI-BASE-01b）；读屏和底下小字按全站一套说法：没有照片“暂无照片”，有照片但打不开“照片暂时打不开”。
 */
/** compact：补充叮嘱那一条身份栏里的小头像（UI-BASE-01b，W-22）——不压在头图上、不带底下的小字（名字就写在旁边），读屏说法照旧。 */
function ReceptionPortrait({ name, photoUrl, origin, species, compact = false }: { name: string; photoUrl: string | null; origin: PetOrigin | null; species: PetSpecies | null; compact?: boolean }) {
  const { step, photoFailed, next } = useAvatarStep(avatarChain(photoUrl, origin, species));
  const pic = step.kind === "photo" || step.kind === "demo" ? step.src : null;
  const art = step.kind === "species" ? step.species : null;
  const label = pic ? `${name}的当前头像` : art ? speciesArtLabel(art, name) : photoFailed ? `${name}，照片暂时打不开` : `${name}，暂无照片`;
  return (
    <div className={`ps-reception-portrait${compact ? " is-compact" : ""}${pic ? " has-photo" : art ? " is-species" : ""}`} aria-label={label} data-species={art ?? undefined}>
      {pic ? <img key={pic} src={pic} alt={name} onError={next} /> : art ? <SpeciesIllustration species={art} /> : <span className="ps-reception-portrait__empty"><PawBadge size={compact ? 36 : 64} /></span>}
      {compact ? null : <span className="ps-reception-portrait__caption">{pic ? name : photoFailed ? "照片暂时打不开" : "暂无照片"}</span>}
    </div>
  );
}

/** 入住接待（第一次）的开场头图：场景插画、头像卡、「入住准备 · 03 / 04」和四站步骤条。补充叮嘱不用它（见 SupplementWho）。 */
function ReceptionHero({ name, photoUrl, origin, species }: { name: string; photoUrl: string | null; origin: PetOrigin | null; species: PetSpecies | null }) {
  return (
    <section className="ps-reception-hero" aria-label="入住接待场景">
      <img className="ps-reception-hero__scene" src={receptionWorld} alt="" />
      <div className="ps-reception-hero__label"><span aria-hidden="true">✦</span> PetSoul · 入住接待</div>
      <ReceptionPortrait name={name} photoUrl={photoUrl} origin={origin} species={species} />
      <div className="ps-reception-hero__caption">
        <div>
          <span className="ps-reception-hero__eyebrow">{entryStepKicker(3)}</span>
          <h1>{`先认识 ${name}`}</h1>
          <p>把你知道的，慢慢说给这个世界听。</p>
          <EntrySteps step={3} className="ps-reception-hero__steps" />
        </div>
      </div>
    </section>
  );
}

/**
 * 补充叮嘱（住进来以后再来交代）：不要大开场头图（UI-BASE-01b，W-22，c84a 10-09 实走：头图占掉大半屏，对话和输入框被挤到后面）。
 * 缩成一条宠物身份栏——小头像（同一条取图链、同一套读屏说法）＋「再记下一件小事」＋名字（这一页的 h1）；接待员卡和对话跟着上来，输入框照旧在贴底区。
 * 不写「入住准备 · 03 / 04」、不放四站步骤条（补充不是入住的一站）。
 */
function SupplementWho({ name, photoUrl, origin, species }: { name: string; photoUrl: string | null; origin: PetOrigin | null; species: PetSpecies | null }) {
  return (
    <section className="ps-reception-who" aria-label="补充叮嘱的伙伴">
      <ReceptionPortrait name={name} photoUrl={photoUrl} origin={origin} species={species} compact />
      <div className="ps-reception-who__text">
        <span className="ps-reception-who__eyebrow">再记下一件小事</span>
        <h1>{`再聊聊 ${name}`}</h1>
      </div>
    </section>
  );
}

/**
 * 服务端的接待说明（host.disclosure，后端原文）已经讲过“引导模式”时，前端不再补那一句。
 * 这是拿来匹配后端文字的，不上页面：后端现在写“引导便笺模式”（后端文案，前端不改写），以后改叫“引导叮嘱 / 记录模式”也认得。
 */
const SERVER_EXPLAINS_GUIDED = /引导(?:便笺|叮嘱|记录)模式/;

/** 接待员身份（AI 角色）始终可见；记录方式的说明收进“怎么记”，不在首屏堆满说明文字。 */
function HostIntro({ session }: { session: ReceptionSession }) {
  return (
    <aside className="ps-host">
      <div className="ps-host__avatar" aria-hidden="true"><Icon name="sparkle" size={19} /></div>
      <div className="ps-host__body">
        {/* 名字和身份一样时（演示里都是「星球接待员」）只写一遍（UI-BASE-01b，W-15） */}
        <div className="ps-host__title"><strong>{session.host.display_name}</strong>{session.host.role_label && session.host.role_label !== session.host.display_name ? <span>{session.host.role_label}</span> : null}</div>
        <details className="ps-host__about">
          <summary>接待员会怎么记你说的话</summary>
          <p className="ps-host__disclosure">{session.host.disclosure}</p>
          {session.mode === "guided_notes" && !SERVER_EXPLAINS_GUIDED.test(session.host.disclosure) ? <p className="ps-host__mode">你的话会原样进入待确认页，不会被当作 TA 已经说过的话。</p> : null}
        </details>
      </div>
    </aside>
  );
}

function Conversation({ session }: { session: ReceptionSession }) {
  return (
    <div className="ps-reception-chat">
      <div className="ps-reception-section-title"><span>你和接待员</span><small>想到什么说什么 · 可以跳过</small></div>
      {/* 输入说明放在对话前面（2026-09-24 巡检 P2）：原来在对话末尾，正好落在贴底区的上沿，首屏被压住一半；
          放在这里，滚到哪儿都不会卡在贴底区底下（输入框的读屏说明仍由 aria-describedby 连上）。 */}
      <p id="reception-input-hint" className="ps-reception-hint">说一件，就整理成一条待你确认的叮嘱；不说也可以，以后还能补充</p>
      <ol className="ps-turns" aria-label="接待对话">
        {session.turns.map((turn) => (
          <li key={turn.turn_id} className={`ps-turn ps-turn--${turn.speaker}`}>
            {turn.text}
          </li>
        ))}
      </ol>
    </div>
  );
}

/** 还没结束、可以接着说的接待（与后端开场时“同一只已有进行中的会话就返回它”认的是同两种状态）。 */
const OPEN_STATUSES: readonly ReceptionSession["status"][] = ["active", "awaiting_confirmation"];

/** 输入框最多长到这么高（约 5 行），再多就在框里滚动：贴底区不能吃掉整屏。 */
const COMPOSER_MAX_HEIGHT = 132;

/**
 * 输入框和“交代这一句”放在贴底区里（2026-09-24 巡检 P1：原来它们在正文末尾，首屏正好落在贴底按钮下面，
 * 320×568 上更是整个在首屏之外）。贴底区始终在屏幕底部，发送键任何时候都看得见、点得到。
 */
function Composer({ session, scope }: { session: ReceptionSession; scope: ReceptionScope }) {
  const guard = useReceptionGuard(scope);
  const { reception } = useServices();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const keyRef = useRef(newIdempotencyKey("reception-turn"));
  const send = useMutation({
    mutationFn: (attempt: ReceptionAttempt) => attempt.run(() => reception.addTurn(session.session_id, { text, expected_revision: session.draft_revision }, keyRef.current)),
    onSuccess: (next, attempt) => {
      if (!attempt.current()) return;
      keyRef.current = newIdempotencyKey("reception-turn");
      setText("");
      queryClient.setQueryData(queryKeys.receptionFor(scope.parts, session.session_id), next);
    },
    // 别处（另一个标签页）改过便笺：重新读取当前版本，输入框里的话保留，由主人再发一次。
    onError: (error, attempt) => {
      if (!attempt.current()) return;
      if (toApiError(error).code === "VERSION_CONFLICT") void queryClient.invalidateQueries({ queryKey: queryKeys.receptionFor(scope.parts, session.session_id) });
    },
  });
  // 一行起步，随内容长高到上限：平时贴底区只占一行输入的高度。
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = "";
    if (input.scrollHeight > 0) input.style.height = `${Math.min(input.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, [text]);
  return (
    <form
      className="ps-composer"
      onSubmit={(event) => {
        event.preventDefault();
        if (guard.current() && text.trim() && !send.isPending) send.mutate(guard.attempt());
      }}
    >
      <label className="visually-hidden" htmlFor="reception-input">
        再交代一件小事
      </label>
      <div className="ps-composer__row">
        <textarea
          ref={inputRef}
          id="reception-input"
          className="ps-composer__input"
          rows={1}
          placeholder="说一件 TA 的小事…"
          aria-describedby="reception-input-hint"
          value={text}
          maxLength={2000}
          onChange={(e) => setText(e.target.value)}
        />
        {/* 发送键是次按钮（UI-BASE-01a，总账 V-02；原来是叶绿按钮）：这一屏的主操作是下面贴底那一颗（整理叮嘱／去入住），一屏只许一个主按钮 */}
        <Button type="submit" variant="secondary" size="sm" loading={send.isPending} disabled={!text.trim()}>
          交代这一句
        </Button>
      </div>
      {send.isError ? (
        <span role="alert" className="ps-composer__note is-error">没发出去，内容还在输入框里</span>
      ) : null}
    </form>
  );
}

/**
 * 接待页有两种用法，靠 `supplement` 分开：
 * - 入住中（新伙伴正在入住，入住阶段不是 active，也没带 ?mode=supplement）：叮嘱记给入住阶段里那只（session.onboarding.pet_id）；
 * - 入住以后来补充（?mode=supplement，或入住阶段已经是 active）：叮嘱记给“当前宠物”——通讯器、我的、小窝用的同一个家庭上下文，
 *   也就是切换栏选中的那只（2026-09-24 巡检 P0：切到豆包再补充，原来记到了入住阶段里记着的年糕身上）。
 *   接待页是不挂主布局的全屏页，没有家庭上下文，所以补充时在这里包一层 HouseholdProvider（两只以上时顶上也有切换栏）。
 */
export function ReceptionPage() {
  const [params] = useSearchParams();
  const account = useSessionState();
  const onboarding = account.data?.onboarding ?? null;
  const live = env.dataMode === "live";
  const supplement = params.get("mode") === "supplement" || onboarding?.step === "active";
  const userId = account.data?.authenticated ? account.data.user?.user_id ?? null : null;
  const { reception } = useServices();
  const scope = useReceptionScope(reception, userId);
  if (live && account.data && !account.data.authenticated) return <Navigate to="/welcome" replace />;
  if (live && onboarding?.step === "needs_companion") return <Navigate to="/onboarding" replace />;
  if (!scope.enabled) return <LoadingState lines={2} label="接待员正在准备…" />;
  if (live && supplement && userId) {
    return (
      <HouseholdProvider userId={userId}>
        <ReceptionView supplement scope={scope} />
      </HouseholdProvider>
    );
  }
  return <ReceptionView supplement={supplement} scope={scope} />;
}

function ReceptionView({ supplement, scope }: { supplement: boolean; scope: ReceptionScope }) {
  const account = useSessionState();
  const current = useOptionalCurrentHousehold()?.pet ?? null;
  const [params] = useSearchParams();
  const petId = supplement ? current?.pet_id ?? account.data?.onboarding?.pet_id : account.data?.onboarding?.pet_id;
  return <ReceptionViewFor key={JSON.stringify([scope.key, petId, supplement, params.get("branch"), params.get("mode"), account.data?.onboarding?.reception_session_id])} supplement={supplement} scope={scope} />;
}

function ReceptionViewFor({ supplement, scope }: { supplement: boolean; scope: ReceptionScope }) {
  const guard = useReceptionGuard(scope);
  const { reception, world, pets } = useServices();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const account = useSessionState();
  const current = useOptionalCurrentHousehold()?.pet ?? null;
  const onboarding = account.data?.onboarding ?? null;
  const live = env.dataMode === "live";
  // live：入住中用入住阶段里的那只（伙伴刚建立、尚未入住，不能读家园快照）；补充时用当前宠物。分支由那只宠物的来源决定。
  const livePetId = supplement ? current?.pet_id ?? onboarding?.pet_id : onboarding?.pet_id;
  const liveOrigin = supplement && current ? current.origin : onboarding?.pet_origin;
  const adoptedPet = liveOrigin ? liveOrigin !== "own_pet" : params.get("branch") === "adopted";
  const branch: ReceptionBranch = (live ? adoptedPet : params.get("branch") === "adopted") ? "adopted" : "own_pet";
  // 每只宠物各用一把开场幂等键：补充时在顶上切到另一只，是另一次接待，不沿用上一只的键。
  const startKeys = useRef(new Map<string, string>());
  const startKey = (id: string) => {
    let key = startKeys.current.get(id);
    if (!key) {
      key = newIdempotencyKey("reception-start");
      startKeys.current.set(id, key);
    }
    return key;
  };
  // 演示模式读演示家园快照；显式传当前宠物（演示下没有家庭上下文，就是 null，结果不变）。
  const home = useQuery({ queryKey: queryKeys.home, queryFn: () => guard.run(() => world.home(current?.pet_id ?? null)), enabled: !live && branch === "own_pet" });
  const petId = live ? livePetId ?? undefined : branch === "adopted" ? "fx-adopt-pet" : home.data?.pet.pet_id;
  const petProfile = useQuery({ queryKey: queryKeys.petProfile(petId ?? "-"), queryFn: () => guard.run(() => pets.publicProfile(petId!)), enabled: Boolean(petId), retry: false });
  // 先接回这只宠物还没结束的接待（2026-09-24 巡检 P1：原来每进一次接待页就新开一次，同一只连开了 6 次，说到一半的话可能接不回来）：
  // 入住阶段里记着的会话（reception_session_id）是这只的，就先按编号读回来；是这只的、还没结束就接着用。没有、读不到或已经结束才新开。
  const resumeId = live && petId && onboarding?.pet_id === petId ? onboarding.reception_session_id ?? null : null;
  const resume = useQuery({
    queryKey: queryKeys.receptionResumeFor(scope.parts, resumeId ?? "-"),
    queryFn: () => guard.run(() => reception.get(resumeId!)),
    enabled: scope.enabled && Boolean(resumeId),
    staleTime: Infinity,
    retry: false,
  });
  const resumable = resume.data && resume.data.pet_id === petId && OPEN_STATUSES.includes(resume.data.status) ? resume.data : null;
  const start = useQuery({
    queryKey: queryKeys.receptionStartFor(scope.parts, branch, petId ?? "-"),
    queryFn: () => guard.run(() => reception.start({ pet_id: petId!, branch }, startKey(petId!))),
    enabled: scope.enabled && Boolean(petId) && (!resumeId || resume.isError || (resume.isSuccess && !resumable)),
    staleTime: Infinity,
  });
  const opened = resumable ?? start.data;
  const sessionId = opened?.session_id;
  const session = useQuery({ queryKey: queryKeys.receptionFor(scope.parts, sessionId ?? "-"), queryFn: () => guard.run(() => reception.get(sessionId!)), enabled: scope.enabled && Boolean(sessionId), initialData: opened });
  // 账号里已经有住进来的宠物、这一只是后加的：给一个“先不加了”的出口回地图，当前宠物还是原来那只（第一次入住没有这个出口）。
  const returnTo = live && !supplement ? petToReturnTo(onboarding, account.data?.user?.user_id) : null;
  const skip = useMutation({
    mutationFn: (attempt: ReceptionAttempt) => attempt.run(() => reception.skip(sessionId!)),
    onSuccess: async (_result, attempt) => {
      if (!attempt.current()) return;
      // 补充叮嘱：和左上角返回同一条规矩——站内有来路就回来路，直接打开时回“我的”；演示模式的入住接待回地图首页；入住中照旧去入住。
      if (supplement) return leaveSupplement(navigate, hasInAppHistory(location) ? 1 : 0);
      if (!live) return navigate("/map");
      await queryClient.invalidateQueries({ queryKey: queryKeys.session });
      if (!attempt.current()) return;
      navigate("/onboarding/move-in");
    },
  });

  const error = account.error ?? home.error ?? start.error ?? session.error;
  const name = petProfile.data?.display_name ?? home.data?.pet.name ?? "TA";
  // 头像按这只（petId）自己的来历认：live 下 liveOrigin 和 petId 是同一只（补充时是当前宠物，入住中是入住阶段里那只）；
  // 演示不认来历（没照片时本来就是演示小灰猫）。物种用 TA 公开主页上的。
  const portraitOrigin = live ? liveOrigin ?? null : null;
  const portraitSpecies = petProfile.data?.species ?? null;
  const count = session.data?.candidates.length ?? 0;
  return (
    <Page bare className="ps-entry-page ps-reception-page">
      <TopBar title={supplement ? "补充叮嘱" : "入住接待"} heading={false} subtitle={branch === "adopted" ? "迎接新伙伴" : "交代一些只有你知道的小事"} back={supplement ? SUPPLEMENT_PARENT : live ? undefined : "/onboarding"} />
      {supplement ? (
        <SupplementWho name={name} photoUrl={petProfile.data?.avatar_url ?? home.data?.pet.photo_url ?? null} origin={portraitOrigin} species={portraitSpecies} />
      ) : (
        <ReceptionHero name={name} photoUrl={petProfile.data?.avatar_url ?? home.data?.pet.photo_url ?? null} origin={portraitOrigin} species={portraitSpecies} />
      )}
      {error ? (
        <ErrorState error={error} onRetry={() => {
          if (account.isError) void account.refetch();
          if (home.isError) void home.refetch();
          if (start.isError) void start.refetch();
          if (session.isError && sessionId) void session.refetch();
        }} />
      ) : !session.data ? (
        <LoadingState lines={2} label="接待员正在准备…" />
      ) : (
        <>
          <div className="ps-stack ps-reception-body">
            <HostIntro session={session.data} />
            <Conversation session={session.data} />
            <DataOriginBadge origin={session.data.data_origin} label={session.data.data_origin === "fixture" ? "演示接待：接待员的话是预先写好的" : "引导记录：原话原样记下，规则只给建议"} />
          </div>
          {/* 贴底区：输入框在上（发送键始终露在外面），主行动在下。主行动随状态变化：有待确认的叮嘱才去整理；一句没说时不把人引到空的确认页。 */}
          <div className="ps-entry-dock ps-reception-dock">
            <Composer key={session.data.session_id} session={session.data} scope={scope} />
            <div className="ps-reception-dock__actions" role="group" aria-label={supplement ? "补充叮嘱的下一步" : "接待的下一步"}>
              {count > 0 ? (
                <>
                  {/* 补充时带上 mode=supplement：整理页在接待读到之前先按它显示，读到后以这次接待的宠物为准。 */}
                  <Button variant="primary" block icon="bookmark" onClick={() => navigate(`/onboarding/notes?session=${encodeURIComponent(session.data!.session_id)}${supplement ? "&mode=supplement" : ""}`, { state: trailFromReception(location) })}>
                    整理这 {count} 条叮嘱
                  </Button>
                  <Button variant="ghost" block loading={skip.isPending} onClick={() => { if (guard.current()) skip.mutate(guard.attempt()); }}>
                    {supplement ? "先回去（以后还能补充）" : live ? "跳过，直接去入住（以后还能补充）" : "先去地图看看（以后还能补充）"}
                  </Button>
                </>
              ) : (
                <Button variant="primary" block icon={supplement ? "back" : live ? "home" : "pin"} loading={skip.isPending} onClick={() => { if (guard.current()) skip.mutate(guard.attempt()); }}>
                  {supplement ? "先回去" : live ? `先带 ${name} 去入住` : "先去地图看看"}
                </Button>
              )}
              {returnTo ? (
                <Button variant="ghost" block icon="back" onClick={() => navigate("/map")}>
                  先不加了，回到 {returnTo.name}
                </Button>
              ) : null}
              {skip.isError ? <p role="alert" className="ps-form-error ps-entry-dock__error"><strong>这一步没能完成，可以再点一次。</strong><span>{toApiError(skip.error).playerMessage}</span></p> : null}
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
